import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@vc/lib/utils";
import { useResponsiveDevice } from "@vc/viewmodels/hooks/useResponsiveDevice";

/**
 * Mobile Overlay Panel Component
 * 
 * This component provides a mobile-optimized overlay panel that slides in from
 * the bottom (portrait) or right (landscape). It's designed specifically for mobile devices.
 * 
 * Auto-positioning: Phone portrait = bottom, Phone landscape = right
 * Manual position prop overrides auto-positioning for tablets/desktop
 */

interface MobileOverlayPanelProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  position?: "right" | "left" | "bottom" | "auto";
  /**
   * Whether this panel contains input fields that need keyboard handling
   * When true, uses viewport-aware height and smooth transitions
   */
  hasInputs?: boolean;
}

export function MobileOverlayPanel({ 
  open, 
  onClose, 
  title,
  children, 
  className,
  position = "auto",
  hasInputs = false
}: MobileOverlayPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);
  const touchCurrentY = useRef<number>(0);
  const touchStartX = useRef<number>(0);
  const touchCurrentX = useRef<number>(0);

  // Auto-detect phone landscape for positioning
  const { isMobileDevice, isLandscape } = useResponsiveDevice();
  const isPhoneLandscape = isMobileDevice && isLandscape;
  
  // Determine actual position: auto = bottom (portrait) / right (landscape)
  const actualPosition = position === "auto" 
    ? (isPhoneLandscape ? "right" : "bottom")
    : position;

  // Handle escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      onClose();
    }
  }, [open, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  // Prevent body scroll when open - only for side panels, not bottom sheets
  useEffect(() => {
    if (open && actualPosition !== 'bottom') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, actualPosition]);

  // Swipe gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchCurrentY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchCurrentX.current = e.touches[0].clientX;
    touchCurrentY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    const diffX = touchStartX.current - touchCurrentX.current;
    const diffY = touchStartY.current - touchCurrentY.current;
    const threshold = 80; // Minimum swipe distance

    // Swipe left to close (when panel is on right)
    if (actualPosition === "right" && diffX > threshold) {
      onClose();
    }
    // Swipe right to close (when panel is on left)
    if (actualPosition === "left" && diffX < -threshold) {
      onClose();
    }
    // Swipe down to close (when panel is on bottom)
    if (actualPosition === "bottom" && diffY < -threshold) {
      onClose();
    }
  };

  return createPortal(
    <div 
      className={cn(
        "fixed inset-0 z-50",
        open ? "pointer-events-auto" : "pointer-events-none"
      )}
      style={{
        backgroundColor: open ? 'rgba(0, 0, 0, 0.6)' : 'transparent',
        transition: 'background-color 300ms ease-out'
      }}
    >
      {/* Backdrop - No blur for performance */}
      <div 
        className="absolute inset-0"
        onClick={onClose}
      />
      
      {/* Overlay Panel - GPU accelerated with viewport-aware height */}
      <div 
        ref={panelRef}
        className={cn(
          "bg-pannelcolor shadow-lg will-change-transform flex flex-col",
          actualPosition === "bottom"
            ? "absolute bottom-0 left-0 right-0 w-full rounded-t-2xl" 
            : "absolute top-0 bottom-0 w-[85vw] rounded-l-xl max-w-[360px]",
          actualPosition === "right" && "right-0",
          actualPosition === "left" && "left-0",
          hasInputs && actualPosition === "bottom" && "mobile-sheet-container",
          className
        )}
        style={{
          transform: actualPosition === "bottom" 
            ? (open ? 'translateY(0)' : 'translateY(100%)')
            : actualPosition === "right"
            ? (open ? 'translateX(0)' : 'translateX(100%)')
            : (open ? 'translateX(0)' : 'translateX(-100%)'),
          // Synchronized transition timing - critical for smooth behavior
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          // CRITICAL: Use FIXED max-height, let content scroll
          // This prevents container resize which causes blinking
          height: actualPosition === "bottom" 
            ? (hasInputs 
                ? '100dvh' // Full viewport height
                : 'min(82vh, calc(100vh - 60px))')
            : undefined,
          maxHeight: actualPosition === "bottom"
            ? (hasInputs
                ? 'var(--viewport-height, 100vh)' // Viewport-aware
                : undefined)
            : undefined,
          // Minimal will-change for better performance
          willChange: 'transform'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Handle bar for swipe indicator - shown only for bottom position */}
        {actualPosition === "bottom" && (
          <div className="mobile-sheet-header flex-shrink-0 flex items-center justify-center py-2">
            <div className="h-1 w-12 rounded-full bg-slate-300" />
          </div>
        )}

        {/* Header with Title and Close Button - bottom position style */}
        {actualPosition === "bottom" && title && (
          <div className="mobile-sheet-header flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-border bg-pannelcolor">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground/80">{title}</h3>
            <button
              onClick={onClose}
              className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-slate-500 hover:bg-slate-200 dark:text-muted-foreground dark:hover:bg-muted transition"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Header for side panels (right/left) */}
        {(actualPosition === "right" || actualPosition === "left") && title  && (
          <div className="mobile-sheet-header flex-shrink-0 flex items-center rounded-t-xl justify-between px-4 py-3 border-b border-slate-200 dark:border-border bg-pannelcolor">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground/80">{title}</h3>
            <button
              onClick={onClose}
              className="grid h-8 w-8 cursor-pointer place-items-center rounded-full text-slate-500 hover:bg-slate-200 dark:text-muted-foreground dark:hover:bg-muted transition"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Content - Scrollable when content exceeds available space */}
        <div className={cn(
          "flex-1 min-h-0",
          hasInputs ? "mobile-sheet-content mobile-keyboard-aware" : "overflow-y-auto"
        )}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
